"use client";

import { useMemo } from "react";
import { CalendarEvent } from "../types/calendar.js";
import { 
  normalizeStringAttendees, 
  normalizeStringOrganizer,
  type AttendeeData 
} from "../lib/attendee-utils.js";

interface UseAttendeeDataReturn {
  attendees: AttendeeData[];
  organizer: AttendeeData | null;
  allAttendees: AttendeeData[];
  totalCount: number;
  formatCount: () => string;
}

/**
 * Hook that normalizes and processes attendee data from CalendarEvent
 * Handles both new structured data and legacy string-based data
 */
export function useAttendeeData(event: CalendarEvent): UseAttendeeDataReturn {
  return useMemo(() => {
    // Use new structured data if available, fallback to legacy format
    const attendees: AttendeeData[] = event.attendeeData 
      ? event.attendeeData 
      : normalizeStringAttendees(event.attendees);
    
    const organizer: AttendeeData | null = event.organizerData 
      ? event.organizerData 
      : normalizeStringOrganizer(event.organizer);

    // Combine organizer and attendees, ensuring organizer is first
    const allAttendees = organizer 
      ? [organizer, ...attendees.filter(a => a.email !== organizer.email)]
      : attendees;

    const totalCount = allAttendees.length;

    const formatCount = () => {
      if (totalCount === 0) return "No attendees";
      if (totalCount === 1) return "1 attendee";
      return `${totalCount} attendees`;
    };

    return {
      attendees,
      organizer,
      allAttendees,
      totalCount,
      formatCount
    };
  }, [event.attendeeData, event.attendees, event.organizerData, event.organizer]);
}
